import { useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../../contexts/AuthContext';
import { BannerContext } from '../../contexts/BannerContext';
import {
  formatConnectTradeConnectError,
  persistConnectTradeConnectError,
  readConnectTradeCallbackParams,
  stripConnectTradeCallbackParams,
} from '../../utils/connectTradeCallbackErrors';

/**
 * Handles return from Connect Trade OAuth on /trade, /onboarding, or /dashboard.
 */
export default function ConnectTradeCallbackHandler() {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const { user, refreshUserSettings } = useContext(AuthContext);
  const { showMessage } = useContext(BannerContext);
  const queryClient = useQueryClient();
  const handled = useRef(false);

  useEffect(() => {
    const callback = readConnectTradeCallbackParams(search);
    if (!callback.hasCallback) return;
    if (handled.current) return;
    handled.current = true;

    const stripQuery = () => {
      const nextSearch = stripConnectTradeCallbackParams(search);
      navigate({ pathname, search: nextSearch }, { replace: true });
    };

    if (callback.ctError) {
      const message = formatConnectTradeConnectError(callback);
      if (pathname.startsWith('/onboarding')) {
        // Onboarding renders the error inline on the connect step (StepTrialConnect).
        persistConnectTradeConnectError(message);
      } else {
        // On /trade (and /dashboard) the connect modal is gone after the OAuth redirect,
        // so surface the error as a persistent banner.
        showMessage(message, 'error', false);
      }
      stripQuery();
      return;
    }

    if (!callback.ctSuccess) {
      stripQuery();
      return;
    }

    if (!user?.token) {
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        queryClient.removeQueries({ queryKey: ['accounts'] });
        queryClient.removeQueries({ queryKey: ['balances'] });
        queryClient.removeQueries({ queryKey: ['orders'] });
        queryClient.removeQueries({ queryKey: ['positions'] });
        await refreshUserSettings?.();
        showMessage('Broker connected via Connect Trade.', 'success');
      } catch (_) {
        showMessage('Broker may be connected — refresh if account data does not load.', 'warning');
      } finally {
        stripQuery();
      }
    })();
  }, [search, pathname, navigate, user?.token, refreshUserSettings, showMessage, queryClient]);

  return null;
}
